<template>
  <content-card :title="$t('realTimeEnergyPrices.title')"
                icon="query_stats" v-if="show && prices !== null">
    <template v-slot:titleBar>
      <v-icon class="price_info material-icons-outlined" color="primary" @click="openSpotmarketDialog()">
        info
      </v-icon>
    </template>
    <template v-slot:content>
      <div class="wrapper px-5 py-3" v-if="hasValues">
        <div class="actual">
          <div :class="[getPriceColorClass(current)]">{{ current }} {{ $t('realTimeEnergyPrices.unit') }}</div>
          <div>{{ $t('realTimeEnergyPrices.now') }}</div>
        </div>
        <div :class="['trend', 'pr-10', 'pt-2', getPriceColorClass(next)]">
          <span class="pr-4">{{ diff }} {{ $t('realTimeEnergyPrices.unit') }}</span>
          <v-icon :class="[getPriceColorClass(next)]">
            {{ trending }}
          </v-icon>
        </div>
        <div class="next">
          <div :class="[getPriceColorClass(next)]">{{ next }} {{ $t('realTimeEnergyPrices.unit') }}</div>
          <div>{{ getNextHour().format($t('realTimeEnergyPrices.time')) }}</div>
        </div>
      </div>
      <div class="px-5 pb-4">
        <v-btn depressed large :outlined="timescope !== 'today'" class="mr-4 wider-buttons" color="primary"
               @click="setTimescope('today')">
          {{ $t('realTimeEnergyPrices.today') }}
        </v-btn>
        <v-btn class="wider-buttons" depressed large :outlined="timescope !== 'tomorrow'" color="primary"
               @click="setTimescope('tomorrow')">
          {{ $t('realTimeEnergyPrices.tomorrow') }}
        </v-btn>
      </div>
      <line-chart v-if="hasData" ref="chart" :key="chartKey" :options="chartOptions" :data="chartData"></line-chart>
      <div class="mt-12 text-center text-h6" v-else>{{ $t('realTimeEnergyPrices.no-data') }}</div>
      <div v-if="hasData">
        <v-list>
          <!-- Working Price -->
          <v-list-item dense two-line class="ma-0">
            <v-list-item-content v-if="!edit.PRICE_INFO_WORKING_PRICE">
              <v-list-item-title class="primary--text font-weight-bold">
                {{ $t('realTimeEnergyPrices.working-price.label') }}
              </v-list-item-title>
              <v-list-item-subtitle v-if="customPrices.PRICE_INFO_WORKING_PRICE !== null">{{
                  priceDisplays.PRICE_INFO_WORKING_PRICE
                }} {{ $t('real-time-energy-prices.working-price.unit') }}
              </v-list-item-subtitle>
              <v-list-item-subtitle v-else>
                {{ $t('real-time-energy-prices.not-set') }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-content v-else>
              <v-text-field outlined dense
                            hide-details
                            :value="customPrices.PRICE_INFO_WORKING_PRICE"
                            v-model="newCustomPrice.PRICE_INFO_WORKING_PRICE"
              :suffix="$t('real-time-energy-prices.working-price.unit')"/>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn icon @click="togglePriceEdit('PRICE_INFO_WORKING_PRICE')" v-if="!edit.PRICE_INFO_WORKING_PRICE">
                <v-icon color="primary">edit</v-icon>
              </v-btn>
              <div>
                <v-btn icon @click="deletePrice('PRICE_INFO_WORKING_PRICE')"
                       v-if="edit.PRICE_INFO_WORKING_PRICE"
                       :loading="loading.PRICE_INFO_WORKING_PRICE">
                  <v-icon color="primary">delete</v-icon>
                </v-btn>
                <v-btn icon @click="editPrice('PRICE_INFO_WORKING_PRICE')"
                       :disabled="newCustomPrice.PRICE_INFO_WORKING_PRICE === null || newCustomPrice.PRICE_INFO_WORKING_PRICE?.length < 1"
                       :loading="loading.PRICE_INFO_WORKING_PRICE"
                       v-if="edit.PRICE_INFO_WORKING_PRICE">
                  <v-icon color="primary">check</v-icon>
                </v-btn>
              </div>
            </v-list-item-action>
          </v-list-item>
          <!-- Base Price -->
          <v-list-item dense two-line class="ma-0">
            <v-list-item-content v-if="!edit.PRICE_INFO_BASE_PRICE">
              <v-list-item-title class="primary--text font-weight-bold">
                {{ $t('realTimeEnergyPrices.base-price.label') }}
              </v-list-item-title>
              <v-list-item-subtitle v-if="customPrices.PRICE_INFO_BASE_PRICE !== null">{{
                  priceDisplays.PRICE_INFO_BASE_PRICE
                }} {{ $t('real-time-energy-prices.base-price.unit') }}
              </v-list-item-subtitle>
              <v-list-item-subtitle v-else>
                {{ $t('real-time-energy-prices.not-set') }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-content v-else>
              <v-text-field outlined dense
                            hide-details
                            :value="customPrices.PRICE_INFO_BASE_PRICE"
                            v-model="newCustomPrice.PRICE_INFO_BASE_PRICE"
                            :suffix="$t('real-time-energy-prices.base-price.unit')"/>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn icon @click="togglePriceEdit('PRICE_INFO_BASE_PRICE')" v-if="!edit.PRICE_INFO_BASE_PRICE">
                <v-icon color="primary">edit</v-icon>
              </v-btn>
              <div>
                <v-btn icon @click="deletePrice('PRICE_INFO_BASE_PRICE')"
                       v-if="edit.PRICE_INFO_BASE_PRICE"
                       :loading="loading.PRICE_INFO_BASE_PRICE">
                  <v-icon color="primary">delete</v-icon>
                </v-btn>
                <v-btn icon @click="editPrice('PRICE_INFO_BASE_PRICE')"
                       :disabled="newCustomPrice.PRICE_INFO_BASE_PRICE === null || newCustomPrice.PRICE_INFO_BASE_PRICE?.length < 1"
                       :loading="loading.PRICE_INFO_BASE_PRICE"
                       v-if="edit.PRICE_INFO_BASE_PRICE">
                  <v-icon color="primary">check</v-icon>
                </v-btn>
              </div>
            </v-list-item-action>
          </v-list-item>
          <v-list-item dense two-line class="ma-0">
            <v-list-item-content>
              <v-list-item-title class="primary--text font-weight-bold">
                {{$t('realTimeEnergyPrices.showStockMarketPrices')}}
              </v-list-item-title>
            </v-list-item-content>
            <v-list-item-action>
              <v-switch 
              :disabled="customPrices.PRICE_INFO_WORKING_PRICE === null || newCustomPrice.PRICE_INFO_BASE_PRICE?.length < 1"
              v-model="showStockMarketPrices" @change="updateChartKey"></v-switch>
            </v-list-item-action>
          </v-list-item>
        </v-list>

      </div>
    </template>
  </content-card>
</template>


<script>
import {Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale} from 'chart.js'

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

import {Line as LineChart} from 'vue-chartjs'
import config from '@/config/config.app.json'
import ContentCard from "@/templates/components/ContentCard.vue";
import moment from 'moment';

export default {
  name: "RealTimeEnergyPrices",
  components: {ContentCard, LineChart},
  data: () => ({
    chartKey: 1,
    showStockMarketPrices: false,
    timescope: 'today',
    prices: null,
    customPrices: {
      PRICE_INFO_WORKING_PRICE: null,
      PRICE_INFO_BASE_PRICE: null
    },
    edit: {
      PRICE_INFO_WORKING_PRICE: false,
      PRICE_INFO_BASE_PRICE: false
    },
    newCustomPrice: {
      PRICE_INFO_WORKING_PRICE: null,
      PRICE_INFO_BASE_PRICE: null
    },
    loading: {
      data: false,
      PRICE_INFO_WORKING_PRICE: false,
      PRICE_INFO_BASE_PRICE: false
    },
    priceDisplays: {
      PRICE_INFO_WORKING_PRICE: null,
      PRICE_INFO_BASE_PRICE: null
    }
  }),
  methods: {
    updateChartKey() {
      this.chartKey++;
    },
    setTimescope(scope) {
      this.timescope = scope
      this.updateChartKey();
    },
    openSpotmarketDialog() {
      this.$root.bisadialog.toggle('infoText', true, {
        title: this.$t('realTimeEnergyPrices.sportmarket.title'),
        icon: 'query_stats',
        html: this.$t('realTimeEnergyPrices.sportmarket.text')
      })
    },
    getData() {
      this.loading.data = true
      let promises = []

      let spotmarketPromise = new Promise((resolve, reject) => {
        this.$rhRequest.sendGet({
          endpoint: "spotmarkt",
        }, (response) => {
          this.prices = response.data?.data
          resolve()
        }, (err) => {
          console.error(err);
          reject(err)
        })
      })
      promises.push(spotmarketPromise)
      let basePricePromise = new Promise((resolve, reject) => {
        this.$rhRequest.sendGet({
              endpoint: "site/get-additional-site-data"
            }, (response) => {
              if (response?.data?.data) {
                let workingPrice = response.data.data.PRICE_INFO_WORKING_PRICE
                let basePrice = response.data.data.PRICE_INFO_BASE_PRICE
                this.priceDisplays.PRICE_INFO_WORKING_PRICE = workingPrice
                this.priceDisplays.PRICE_INFO_BASE_PRICE = basePrice
                workingPrice = !workingPrice ? null : workingPrice.replace(',', '.')
                basePrice = !basePrice ? null : basePrice.replace(',', '.')
                this.customPrices.PRICE_INFO_WORKING_PRICE = isNaN(parseFloat(workingPrice)) ? null : parseFloat(workingPrice)
                this.customPrices.PRICE_INFO_BASE_PRICE = isNaN(parseFloat(basePrice)) ? null : parseFloat(basePrice)
                resolve()
              }
            }, (err) => {
              console.error(err);
              reject(err)
            }
        )
      })
      promises.push(basePricePromise)
      Promise.all(promises).finally(() => {
        this.loading.data = false
        this.$emit('finishedEnergyPricesLoading', !!this.prices)
      })
      return promises
    },
    getChartData() {
      let dataset = []
      let labels = []
      let colors = []

      for (let i = 0; i < 24; i++) {
        let m = moment().seconds(0).milliseconds(0).minutes(0).hour(i)
        labels.push(m.format(this.$t('realTimeEnergyPrices.time')))
        if (this.timescope === 'tomorrow') {
          m.add(1, 'day')
        }
        let current = moment().seconds(0).milliseconds(0).minutes(0)

        let price = (this.prices[m.unix() * 1000])
        if (typeof price !== 'undefined' && price !== null) {
          price = Math.round(price[1] * 100)
          let endPrice = price
          dataset.push(endPrice)

          let transparent = '70';
          if (m.unix() === current.unix()) {
            transparent = 'FF'
          }

          let color
          if (endPrice >= this.priceMarker) {
            color = this.getThemeColor('error')
          } else {
            color = this.getThemeColor('success')
          }
          colors.push(`${color}${transparent}`)
        }
      }

      if (!this.showStockMarketPrices && this.customPrices.PRICE_INFO_WORKING_PRICE) {
        dataset = dataset.map((e) => e + this.customPrices.PRICE_INFO_WORKING_PRICE)
      }

      return {
        labels: labels,
        datasets: [{
          data: dataset,
          backgroundColor: colors,
          borderColor: (context) => {
            const chart = context.chart;
            const {ctx, chartArea} = chart;

            if (!chartArea) {
              // This case happens on initial chart load
              return;
            }

            let yAxis = chart.scales['y'];
            // Get the y coordinates for the value 0 and for the value when the gradient color should change
            let yBottom = yAxis.getPixelForValue(0);
            let yTop = yAxis.getPixelForValue(this.priceMarker);
            // Create linear gradient
            let gradient = ctx.createLinearGradient(0, yBottom, 0, yTop);
            // Use minimal color gradient with the color stops between 0.98 and 1
            gradient.addColorStop(0.98, this.getThemeColor('success'));
            gradient.addColorStop(1, this.getThemeColor('warning'));

            return gradient;
          },
          borderWidth: 2,
          stepped: true
        }],
      }
    }
    ,
    getPriceColorClass(price) {
      if (price >= this.priceMarker) {
        return 'price_high'
      } else {
        return 'price_low'
      }
    }
    ,
    getCurrentHour() {
      return moment().seconds(0).milliseconds(0).minutes(0)
    }
    ,
    getNextHour() {
      return this.getCurrentHour().add(1, 'hours')
    }
    ,
    getThemeColor(color) {
      let theme = this.$vuetify.theme.themes.light
      if (this.$vuetify.theme.dark) {
        theme = this.$vuetify.theme.themes.dark
      }

      return theme[color]
    }
    ,

    getPrice(timestamp) {
      if (typeof this.prices[timestamp * 1000] === 'undefined') return null;

      let price = Math.round(this.prices[timestamp * 1000][1] * 100);
      
      if (!this.showStockMarketPrices && this.customPrices.PRICE_INFO_WORKING_PRICE) {
        price = price + this.customPrices.PRICE_INFO_WORKING_PRICE
      }
      
      return price;
    },
    deletePrice(key) {
      this.$rhRequest.sendPost({
            endpoint: "site/remove-additional-site-data",
            data: {
              key: key
            }
          }, () => {
            Promise.all(this.getData()).finally(() => {
              this.edit[key] = false
              this.loading[key] = false
            })
          }, (err) => {
            this.loading[key] = false
            console.error(err);
          }
      )
    },
    editPrice(key) {
      this.loading[key] = true
      this.$rhRequest.sendPost({
            endpoint: "site/set-additional-site-data",
            data: {
              key: key,
              value: this.newCustomPrice[key]
            }
          }, () => {
            Promise.all(this.getData()).finally(() => {
              this.edit[key] = false
              this.loading[key] = false
            })

          }, (err) => {
            this.loading[key] = false
            console.error(err);
          }
      )
    },
    togglePriceEdit(key) {
      this.edit[key] = !this.edit[key]
      this.newCustomPrice[key] = this.priceDisplays[key]
    }
  },
  mounted() {
    this.getData()
  }
  ,
  computed: {
    priceMarker() {
      return 26
    }
    ,
    current() {
      return this.getPrice(this.getCurrentHour().unix())
    }
    ,
    next() {
      return this.getPrice(this.getNextHour().unix())
    }
    ,
    diff() {
      return this.next - this.current;
    }
    ,
    hasValues() {
      return !(this.next === null && this.current === null)
    }
    ,
    show() {
      if (typeof config.hasSpotmarket === 'undefined') return false;
      return JSON.parse(config.hasSpotmarket);
    }
    ,
    trending() {
      if (this.current < this.next) {
        return 'trending_up'
      } else if (this.current > this.next) {
        return 'trending_down'
      } else {
        return 'trending_flat'
      }
    }
    ,
    hasData() {
      return this.getChartData().datasets[0].data.length > 0
    }
    ,
    chartOptions() {
      return {
        responsive: true,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            enabled: true,
            callbacks: {
              label: ((tooltipItems) => {
                return `${tooltipItems.raw} ${this.$t('realTimeEnergyPrices.unit')}`
              })
            }
          }
        },
        scales: {
          y: {
            display: true,
            position: 'right',
            ticks: {
              callback: (val) => {
                return `${val} ${this.$t('realTimeEnergyPrices.unit')}`
              }
            }
          },
        },
      }
    }
    ,
    chartData() {
      return this.getChartData()
    }
  }
}
</script>

<style scoped lang="scss">
@import '~@/styles/real-time-energy-prices.scss';
</style>
